<template>
  <v-container>
    <the-header :title="getLabel('new_collaborator')" :go-back="goBack" />
    <v-card elevation="5" class="rounded-lg">
      <v-sheet elevation="3" class="rounded-lg rounded-b-0" color="primary">
        <v-card-title class="text-h6 white--text">{{ getLabel("create_collaborator") }}</v-card-title>
      </v-sheet>
      <v-divider />
      <v-card-text>
        <v-alert v-if="errors.length" type="error" class="rounded-lg" dismissible @input="closeAlert">
          <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
        </v-alert>
        <v-alert v-if="successes.length" type="success" class="rounded-lg" dismissible @input="closeAlert">
          <div v-for="(success, index) in successes" :key="index">{{ success }}</div>
        </v-alert>
        <v-form ref="form">
          <!-- First Row -->
          <v-row>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.firstname"
                v-bind="collaboratorFieldsProperties('firstname')"
              />
            </v-col>
            <v-col>
              <v-text-field v-model="formNewCollaborator.lastname" v-bind="collaboratorFieldsProperties('lastname')" />
            </v-col>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.mobilephone"
                v-bind="collaboratorFieldsProperties('mobilephone')"
                :error="phoneOrEmailRequired"
              />
            </v-col>
          </v-row>
          <!-- Second Row -->
          <v-row>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.personalEmail"
                v-bind="collaboratorFieldsProperties('personalEmail')"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.email"
                v-bind="collaboratorFieldsProperties('email')"
                :error="phoneOrEmailRequired"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.broadBeanUsername"
                v-bind="collaboratorFieldsProperties('broadBeanUsername')"
              />
            </v-col>
          </v-row>
          <!-- Third Row -->
          <v-row>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.employeeNumber"
                v-bind="collaboratorFieldsProperties('employeeNumber')"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.registrationNumber"
                v-bind="collaboratorFieldsProperties('registrationNumber')"
              />
            </v-col>
            <v-col class="d-flex justify-center">
              <div>
                <v-switch
                  v-model="formNewCollaborator.useSSO"
                  v-bind="collaboratorFieldsProperties('useSSO')"
                />
              </div>
            </v-col>
          </v-row>
          <!-- Fourth Row -->
          <v-row>
            <v-col cols="4">
              <v-text-field v-model="formNewCollaborator.street_number" v-bind="collaboratorFieldsProperties('street_number')" />
            </v-col>
            <v-col cols="8">
              <v-text-field v-model="formNewCollaborator.street_name" v-bind="collaboratorFieldsProperties('street_name')" />
            </v-col>
          </v-row>
          <!-- Fifth Row -->
          <v-row>
            <v-col>
              <v-text-field
                v-model="formNewCollaborator.address1"
                v-bind="collaboratorFieldsProperties('address1')"
              />
            </v-col>
          </v-row>
          <!-- Sixth Row -->
          <v-row>
            <v-col cols="4">
              <v-text-field v-model="formNewCollaborator.town" v-bind="collaboratorFieldsProperties('town')" />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="formNewCollaborator.zip_code" v-bind="collaboratorFieldsProperties('zip_code')" />
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="formNewCollaborator.country"
                v-bind="collaboratorFieldsProperties('country')"
                :items="countries"
                clearable
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-text class="d-flex justify-end">
        <v-btn x-large color="warning" @click="resetForm()">
          <v-icon class="mr-3">mdi-broom</v-icon>
          {{ getLabel("clean") }}
        </v-btn>
        <v-btn x-large class="ml-4" color="success" @click="createCollaborator()">
          <v-icon class="mr-3">mdi-content-save</v-icon>
          {{ getLabel("save") }}
        </v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';
import { useRouter } from 'vue2-helpers/vue-router';

import { useMainStore } from '@/store/index';

import TheHeader from '../UI/TheHeader.vue';

import { countries } from '@/utils/countries';
import { getGraphqlSchema } from '@/graphql/index';
import { collaboratorFieldsProperties } from '@/utils/forms';
import { getLabel } from '@/utils';

export default {
  components: { TheHeader },
  setup (props, context) {
    const store = useMainStore();

    const router = useRouter();

    const errors = ref([]);

    const successes = ref([]);

    const closeAlert = () => {
      errors.value = [];
      successes.value = [];
    };

    const formNewCollaborator = ref({
      firstname: null,
      lastname: null,
      mobilephone: null,
      email: null,
      broadBeanUsername: null,
      employeeNumber: null,
      registrationNumber: null,
      street_name: null,
      street_number: null,
      address1: null,
      town: null,
      zip_code: null,
      country: null,
      useSSO: false
    });

    const goBack = () => {
      router.back();
    };

    const resetForm = () => {
      context.refs.form.reset();
      errors.value = [];
      successes.value = [];
    };

    // Create collaborator
    const { mutate: mutationCreateCollaborator, error: sendMessageError, onDone, onError } = useMutation(getGraphqlSchema('createCollaborator'), () => ({
      variables: {
        firstname: formNewCollaborator.value.firstname.trim(),
        lastname: formNewCollaborator.value.lastname.trim(),
        mobilephone: formNewCollaborator.value.mobilephone,
        email: formNewCollaborator.value.email,
        broadBeanUsername: formNewCollaborator.value.broadBeanUsername,
        employeeNumber: formNewCollaborator.value.employeeNumber,
        registrationNumber: formNewCollaborator.value.registrationNumber,
        useSSO: formNewCollaborator.value.useSSO,
        street_number: formNewCollaborator.value.street_number,
        street_name: formNewCollaborator.value.street_name,
        town: formNewCollaborator.value.town,
        address1: formNewCollaborator.value.address1,
        zip_code: formNewCollaborator.value.zip_code,
        country: formNewCollaborator.value.country
      }
    }));

    onError(() => {
      store.loading = false;

      // when the createCollaborator mutation returns an error
      successes.value = [];
      errors.value = [sendMessageError.value.message];
    });

    onDone(() => {
      store.loading = false;

      // when the creatorCollaborator mutation finishes
      context.refs.form.reset();

      errors.value = [];
      successes.value = [getLabel('collaborator_saved')];
    });

    const triedValidate = ref(false);

    const phoneOrEmailRequired = computed(() => {
      if (triedValidate.value) {
        return !formNewCollaborator.value.mobilephone && !formNewCollaborator.value.email;
      }
      return false;
    });

    const validation = () => {
      triedValidate.value = true;

      const formValidation = context.refs.form.validate();

      if (!formValidation) {
        successes.value = [];
        errors.value = [getLabel('check_fields')];

        return false;
      }

      if (phoneOrEmailRequired.value) {
        successes.value = [];
        errors.value = [getLabel('phone_or_email_required_error')];
      }

      return !phoneOrEmailRequired.value && formValidation;
    };

    const createCollaborator = () => {
      if (validation()) {
        triedValidate.value = false;

        store.loading = true;
        mutationCreateCollaborator();
      }
    };

    return {
      getLabel,
      collaboratorFieldsProperties,
      countries,
      errors,
      successes,
      closeAlert,
      formNewCollaborator,
      goBack,
      resetForm,
      phoneOrEmailRequired,
      createCollaborator
    };
  }
};
</script>

<style lang="scss">
</style>
